import React, { useEffect, useMemo, useState } from "react";

import { useSelector } from "react-redux";

import toast from "react-hot-toast";

import { RootState } from "@/app/store";
import {
  isV2DeviceCommand,
  useGetDeviceDetailQuery,
  useGetDevicesQuery,
  useLazyGetDeviceCommandStatusQuery,
  useSendDeviceCommandMutation,
} from "@/features/devices/devicesApi";
import { isValidIanaTimeZone } from "@/features/devices/ianaTimeZones";
import {
  DEVICE_COMMAND_OPERATOR_COPY,
  persistDeviceCommandJobs,
  clearPersistedDeviceCommandJobs,
  waitForDeviceCommandOperatorResult,
} from "@/features/devices/waitForDeviceCommandOperatorResult";
import {
  canTargetSingleZone,
} from "@/features/screensavers/screensaversApi";
import { buildAssistantPackageUpdateBody } from "@/features/devices/assistantSoftwareModel";
import type { SoftwareInventoryOtaSyncTarget } from "@/features/devices/softwareInventoryOtaSync";
import { buildScreensaverPackageUpdateBody } from "@/features/screensavers/screensaverSoftwareModel";
import {
  ActiveModalNameEnum,
  BrandSoftwareTypeOption,
} from "@/features/ui/types";

import DevicesControlPanel from "../components/DevicesControlPanel/DevicesControlPanel";
import GenericControlPanelModal from "../generic-control-panel/GenericControlPanelModal";

import {
  brandSoftwareOptions,
  brandSoftwareRestartCommandOptions,
  brandSoftwareUpdateCommandOptions,
} from "../../constants";

/** Operator UI: Sending… / Successful / Unsuccessful only. HTTP 202 is never Successful. */
export const DEVICE_COMMAND_OPERATOR_TOAST = DEVICE_COMMAND_OPERATOR_COPY;

interface DeviceModalProps {
  onHide?: () => void;
  onSuccess?: () => void;
  onError?: () => void;
  /** When false, no command is transmitted and no result toast is shown. */
  commandsEnabled: boolean;
  commandsDisabledNotice?: string;
}

/** Normalize modal ids to string deviceIds (never CheckedDevice objects). */
export function resolveDeviceIds(ids: unknown): string[] {
  if (!Array.isArray(ids)) return [];
  return ids
    .map((raw) => {
      if (typeof raw === "string") return raw;
      if (raw && typeof raw === "object" && typeof (raw as { id?: unknown }).id === "string") {
        return (raw as { id: string }).id;
      }
      return null;
    })
    .filter((id): id is string => typeof id === "string" && id.length > 0);
}

const DevicesControlPanelModal: React.FC<DeviceModalProps> = ({
  onHide,
  onSuccess,
  onError,
  commandsEnabled,
  commandsDisabledNotice,
}) => {
  const [selectedOption, setSelectedOption] = useState<BrandSoftwareTypeOption>(
    brandSoftwareOptions[0]
  );
  const [selectedScreensaverId, setSelectedScreensaverId] = useState<
    string | null
  >(null);
  /**
   * Optimistic Current after successful Apply ack (until devices list refreshes).
   * Durable SoT is device.screensaver_active_content_id from Fleet.
   */
  const [ackedContentId, setAckedContentId] = useState<string | null>(null);
  const [ackedReleaseId, setAckedReleaseId] = useState<string | null>(null);
  const [isMutatingScreensaver, setIsMutatingScreensaver] = useState(false);
  const [isMutatingScreensaverSoftware, setIsMutatingScreensaverSoftware] =
    useState(false);
  /**
   * After a successful software OTA terminal ACK, poll package GETs until
   * device inventory Installed matches Available (or timeout). Cleared on
   * fail, modal close, device change, match, or timeout — never assumes
   * installed merely because OTA was sent.
   */
  const [softwareOtaSyncTarget, setSoftwareOtaSyncTarget] =
    useState<SoftwareInventoryOtaSyncTarget | null>(null);

  const handleOnClickOption = (option: BrandSoftwareTypeOption) => {
    setSelectedOption(option);
  };

  const activeModalState = useSelector(
    (state: RootState) => state.ui.activeModalState
  );

  const { name, ids = [] } = activeModalState;
  const deviceIds = resolveDeviceIds(ids);
  const deviceIdsKey = deviceIds.join("|");

  // Zone target changed — clear optimistic ack + inventory sync; durable Current comes from device row.
  useEffect(() => {
    setAckedContentId(null);
    setAckedReleaseId(null);
    setSoftwareOtaSyncTarget(null);
  }, [deviceIdsKey]);

  const { data: devicesData, refetch: refetchDevices } = useGetDevicesQuery(
    { page: 1, pageSize: 500 },
    { skip: !canTargetSingleZone(deviceIds) }
  );

  const deviceRow = useMemo(() => {
    if (!canTargetSingleZone(deviceIds) || !devicesData?.items) return null;
    return devicesData.items.find((d) => d.id === deviceIds[0]) || null;
  }, [devicesData?.items, deviceIds]);

  const activeContentId =
    ackedContentId ||
    deviceRow?.screensaver_active_content_id ||
    null;
  const activeReleaseId =
    ackedReleaseId || deviceRow?.screensaver_release_id || null;
  /** Durable device-confirmed enabled — never inferred from last click. */
  const screensaverEnabled =
    typeof deviceRow?.screensaver_enabled === "boolean"
      ? deviceRow.screensaver_enabled
      : null;
  const deviceAppearsOffline =
    typeof deviceRow?.status === "string" &&
    deviceRow.status.trim().toLowerCase() === "offline";

  const singleDeviceId =
    canTargetSingleZone(deviceIds) && deviceIds.length === 1
      ? deviceIds[0]
      : null;
  const consoleOpen =
    name === ActiveModalNameEnum.Device || name === ActiveModalNameEnum.Release;
  const {
    data: deviceDetail,
    refetch: refetchDeviceDetail,
  } = useGetDeviceDetailQuery(singleDeviceId as string, {
    skip: !singleDeviceId || !consoleOpen,
  });
  const deviceTimeZone = deviceDetail?.detail?.os?.timeZone ?? null;
  const deviceTimeZoneConfirmed =
    deviceDetail?.detail?.os?.timeZoneConfirmed === true;

  const [sendDeviceCommand, { isLoading: isMutatingUpdate }] =
    useSendDeviceCommandMutation();

  const [sendRestartCommand, { isLoading: isMutatingRestart }] =
    useSendDeviceCommandMutation();

  const [getCommandStatus] = useLazyGetDeviceCommandStatusQuery();

  const pollOperatorState = async (commandId: string) => {
    try {
      const res = await getCommandStatus({ commandId }).unwrap();
      return {
        operatorState: res.command?.operatorState,
        activeContentId: res.command?.activeContentId ?? null,
        activeContentVersion: res.command?.activeContentVersion ?? null,
      };
    } catch {
      return null;
    }
  };

  const sendAndAwaitOperatorResult = async (
    send: ReturnType<typeof useSendDeviceCommandMutation>[0],
    command: string,
    extras?: {
      action?: "apply" | "enable" | "disable";
      screensaverId?: string;
      packageName?: string;
      timeZone?: string;
      /** When true, refuse multi-Zone fan-out (screensaver). */
      singleZoneOnly?: boolean;
    }
  ): Promise<{
    ok: boolean;
    activeContentId?: string | null;
    commandId?: string | null;
  }> => {
    if (!isV2DeviceCommand(command)) {
      throw new Error(`Command not available on V2 Device Fleet: ${command}`);
    }
    if (extras?.singleZoneOnly && !canTargetSingleZone(deviceIds)) {
      toast.error("Select exactly one Zone");
      return { ok: false };
    }
    const toastId = toast.loading(DEVICE_COMMAND_OPERATOR_COPY.sending);
    try {
      const targets = extras?.singleZoneOnly ? [deviceIds[0]] : deviceIds;
      const published = await Promise.all(
        targets.map((deviceId) =>
          send({
            deviceId,
            command,
            action: extras?.action,
            screensaverId: extras?.screensaverId,
            packageName: extras?.packageName,
            timeZone: extras?.timeZone,
          }).unwrap()
        )
      );
      // Exception 3: persist commandIds so leave/reload can resume Fleet SoT polling.
      persistDeviceCommandJobs(
        published.map((row, i) => ({
          commandId: String(row.commandId || ""),
          command,
          deviceId: targets[i],
          startedAt: Date.now(),
        })).filter((j) => j.commandId.length > 0)
      );
      const states = await Promise.all(
        published.map((row) =>
          waitForDeviceCommandOperatorResult({
            commandId: row.commandId,
            command,
            action: extras?.action,
            getStatus: pollOperatorState,
            onState: (state) => {
              if (state === "downloading") {
                toast.loading(DEVICE_COMMAND_OPERATOR_COPY.downloading, {
                  id: toastId,
                });
              } else if (state === "sending") {
                toast.loading(DEVICE_COMMAND_OPERATOR_COPY.sending, {
                  id: toastId,
                });
              }
            },
          })
        )
      );
      toast.dismiss(toastId);
      clearPersistedDeviceCommandJobs();
      if (states.every((s) => s === "successful")) {
        toast.success(DEVICE_COMMAND_OPERATOR_COPY.successful);
        let activeContentId: string | null = null;
        const lastId = published[0]?.commandId;
        if (lastId) {
          const finalStatus = await pollOperatorState(String(lastId));
          activeContentId = finalStatus?.activeContentId ?? null;
        }
        return { ok: true, activeContentId, commandId: lastId || null };
      }
      toast.error(DEVICE_COMMAND_OPERATOR_COPY.unsuccessful);
      return { ok: false };
    } catch {
      toast.dismiss(toastId);
      toast.error(DEVICE_COMMAND_OPERATOR_COPY.unsuccessful);
      return { ok: false };
    }
  };

  const runScreensaverAction = async (
    action: "apply" | "enable" | "disable"
  ) => {
    if (!commandsEnabled) return;
    if (action === "apply" && !selectedScreensaverId) {
      toast.error("Select a screensaver first");
      return;
    }
    setIsMutatingScreensaver(true);
    try {
      const result = await sendAndAwaitOperatorResult(
        sendDeviceCommand,
        "update_kk_screensaver",
        {
          action,
          screensaverId:
            action === "apply" ? selectedScreensaverId || undefined : undefined,
          singleZoneOnly: true,
        }
      );
      if (result.ok) {
        // Current updates only after device ack — never on publish alone.
        if (action === "apply") {
          if (result.activeContentId) {
            setAckedContentId(result.activeContentId);
          }
          if (selectedScreensaverId) {
            setAckedReleaseId(selectedScreensaverId);
          }
        }
        await refetchDevices();
        if (onSuccess !== undefined) onSuccess();
      } else if (onError !== undefined) {
        onError();
      }
    } finally {
      setIsMutatingScreensaver(false);
    }
  };

  /** Screensaver APK OTA — Fleet update_kk_package → Assistant ManagedPackageUpdater. */
  const runScreensaverSoftwareUpdate = async () => {
    if (!commandsEnabled) return;
    if (!canTargetSingleZone(deviceIds)) {
      toast.error("Select exactly one Zone");
      return;
    }
    const body = buildScreensaverPackageUpdateBody();
    setIsMutatingScreensaverSoftware(true);
    try {
      setSoftwareOtaSyncTarget("screensaver");
      const result = await sendAndAwaitOperatorResult(
        sendDeviceCommand,
        body.command,
        {
          packageName: body.packageName,
          singleZoneOnly: true,
        }
      );
      if (result.ok) {
        await refetchDevices();
        if (onSuccess !== undefined) onSuccess();
      } else {
        setSoftwareOtaSyncTarget(null);
        if (onError !== undefined) onError();
      }
    } catch {
      setSoftwareOtaSyncTarget(null);
      if (onError !== undefined) onError();
    } finally {
      setIsMutatingScreensaverSoftware(false);
    }
  };

  const handleOnClickUpdate = async () => {
    if (!commandsEnabled) return;

    if (selectedOption.value === "assistant") {
      if (!canTargetSingleZone(deviceIds)) {
        toast.error("Select exactly one Zone");
        return;
      }
      const body = buildAssistantPackageUpdateBody();
      if (!isV2DeviceCommand(body.command)) {
        toast.error(DEVICE_COMMAND_OPERATOR_COPY.unsuccessful);
        return;
      }
      setSoftwareOtaSyncTarget("assistant");
      const result = await sendAndAwaitOperatorResult(
        sendDeviceCommand,
        body.command,
        { singleZoneOnly: true }
      );
      if (result.ok) {
        await refetchDevices();
        if (onSuccess !== undefined) onSuccess();
      } else {
        setSoftwareOtaSyncTarget(null);
        if (onError !== undefined) onError();
      }
      return;
    }

    const command =
      brandSoftwareUpdateCommandOptions.find(
        (c) => c.type === selectedOption.value
      )?.command ?? "";

    if (!isV2DeviceCommand(command)) {
      toast.error(DEVICE_COMMAND_OPERATOR_COPY.unsuccessful);
      return;
    }

    if (selectedOption.value === "krosskast") {
      setSoftwareOtaSyncTarget("player");
    }
    const result = await sendAndAwaitOperatorResult(sendDeviceCommand, command);
    if (result.ok) {
      if (onSuccess !== undefined) onSuccess();
    } else {
      setSoftwareOtaSyncTarget(null);
      if (onError !== undefined) onError();
    }
  };

  const handleOnClickRestart = async () => {
    if (!commandsEnabled) return;
    const command =
      brandSoftwareRestartCommandOptions.find(
        (c) => c.type === selectedOption.value
      )?.command ?? "";

    if (!isV2DeviceCommand(command)) {
      toast.error(DEVICE_COMMAND_OPERATOR_COPY.unsuccessful);
      return;
    }

    const result = await sendAndAwaitOperatorResult(sendRestartCommand, command);
    if (result.ok) {
      if (onSuccess !== undefined) onSuccess();
    } else if (onError !== undefined) {
      onError();
    }
  };

  const [sendRebootCommand, { isLoading: isMutatingReboot }] =
    useSendDeviceCommandMutation();
  const [sendTimezoneCommand, { isLoading: isMutatingTimezone }] =
    useSendDeviceCommandMutation();

  const handleOnClickReboot = async () => {
    if (!commandsEnabled) return;
    if (!isV2DeviceCommand("reboot_device")) {
      toast.error(DEVICE_COMMAND_OPERATOR_COPY.unsuccessful);
      return;
    }
    const result = await sendAndAwaitOperatorResult(
      sendRebootCommand,
      "reboot_device",
      { singleZoneOnly: true }
    );
    if (result.ok) {
      if (onSuccess !== undefined) onSuccess();
    } else if (onError !== undefined) {
      onError();
    }
  };

  const handleSetTimezone = async (timeZone: string) => {
    if (!commandsEnabled) return;
    if (!timeZone || !isValidIanaTimeZone(timeZone)) {
      toast.error("Select a valid IANA time zone");
      return;
    }
    const result = await sendAndAwaitOperatorResult(
      sendTimezoneCommand,
      "set_timezone",
      { timeZone, singleZoneOnly: true }
    );
    if (result.ok) {
      try {
        await refetchDeviceDetail();
      } catch {
        // Display refresh is best-effort; command already succeeded.
      }
      if (onSuccess !== undefined) onSuccess();
    } else if (onError !== undefined) {
      onError();
    }
  };

  const show =
    name === ActiveModalNameEnum.Device || name === ActiveModalNameEnum.Release;

  // Modal closed / destroyed — stop inventory polling timers.
  useEffect(() => {
    if (!show) {
      setSoftwareOtaSyncTarget(null);
    }
  }, [show]);

  const handleHide = () => {
    setSoftwareOtaSyncTarget(null);
    if (onHide !== undefined) onHide();
  };

  const kkCommandsEnabled =
    commandsEnabled && selectedOption.value === "krosskast";
  const assistantCommandsEnabled =
    commandsEnabled && selectedOption.value === "assistant";
  const osCommandsEnabled =
    commandsEnabled && selectedOption.value === "player";
  const screensaverCommandsEnabled = osCommandsEnabled;
  const panelCommandsEnabled =
    selectedOption.value === "assistant"
      ? assistantCommandsEnabled
      : selectedOption.value === "krosskast"
        ? kkCommandsEnabled
        : osCommandsEnabled;

  return (
    <GenericControlPanelModal
      show={show}
      onHide={handleHide}
      leftPanelButtons={brandSoftwareOptions}
      onClickLeftPanelButton={handleOnClickOption}
      selectedOption={selectedOption}
      destroyOnHidden={true}
      rightPanelComponent={
        show ? (
          <DevicesControlPanel
            ids={deviceIds}
            zoneId={deviceRow?.zone_id || null}
            selectedOption={selectedOption}
            isMutatingUpdate={isMutatingUpdate}
            isMutatingRestart={isMutatingRestart}
            isMutatingReboot={isMutatingReboot}
            isMutatingTimezone={isMutatingTimezone}
            onClickUpdate={handleOnClickUpdate}
            onClickRestart={handleOnClickRestart}
            onClickReboot={handleOnClickReboot}
            onSetTimezone={handleSetTimezone}
            deviceTimeZone={deviceTimeZone}
            deviceTimeZoneConfirmed={deviceTimeZoneConfirmed}
            commandsEnabled={panelCommandsEnabled}
            commandsDisabledNotice={
              selectedOption.value === "player" ||
              selectedOption.value === "assistant" ||
              selectedOption.value === "krosskast"
                ? commandsDisabledNotice
                : undefined
            }
            screensaverCommandsEnabled={screensaverCommandsEnabled}
            selectedScreensaverId={selectedScreensaverId}
            activeContentId={activeContentId}
            activeReleaseId={activeReleaseId}
            screensaverEnabled={screensaverEnabled}
            onSelectScreensaver={setSelectedScreensaverId}
            onScreensaverApply={() => runScreensaverAction("apply")}
            onScreensaverEnable={() => runScreensaverAction("enable")}
            onScreensaverDisable={() => runScreensaverAction("disable")}
            onScreensaverSoftwareUpdate={runScreensaverSoftwareUpdate}
            isMutatingScreensaver={isMutatingScreensaver}
            isMutatingScreensaverSoftware={isMutatingScreensaverSoftware}
            softwareOtaSyncTarget={softwareOtaSyncTarget}
            onSoftwareOtaSyncSettled={() => setSoftwareOtaSyncTarget(null)}
            deviceAppearsOffline={deviceAppearsOffline}
            consoleOpen={show}
          />
        ) : null
      }
    />
  );
};

export default DevicesControlPanelModal;
